import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Academy, Coach, CoachAvailability, CoachAvailabilityException, Student } from '@court-sync/shared';
import {
  addDaysYmd,
  formatCurrencySafe,
  isPrivateSlotBlockedOnCalendarDate,
  isSessionStartAtLeastHoursAhead,
} from '@court-sync/shared';
import toast from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { ArrowLeft, Calendar, Clock, GraduationCap, User, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '../../../store/auth';
import { privateBookingService, type PrivateBookingConfig } from '../../../services/privateBooking';
import { studentService } from '../../../services/student';
import { useFormatDate } from '../../../hooks/useFormatDate';
import {
  availabilityWindowsFromCoachRows,
  buildSelectablePrivateSlots,
  calendarPrivateSlotKey,
  getDefaultWeeklyWindowsWhenCoachAvailabilityUnset,
  isPrivateSlotWithinFreeWindows,
  mergeAdjacentAvailabilityWindows,
  privateSlotKey,
  subtractBusyFromWeeklyWindows,
} from './privateBookingScheduleUtils';
import { formatTimeHHMM } from '../../../utils/timeFormat';
import { PrivateBookingWeekCalendar, mondayOfWeekContainingYmd, type CalendarSessionSlot } from './PrivateBookingWeekCalendar';

function localTodayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type Props = {
  academy: Academy;
  academySlug: string;
  config: PrivateBookingConfig;
  coaches: Coach[];
  students: Student[];
  onStudentsRefresh: () => Promise<void>;
  onComplete: (done: { message: string; bookingId?: string }) => void;
  /** Compact single-column layout for org home embed. */
  embedded?: boolean;
};

const STEPS = [
  { n: 1, label: 'Student' },
  { n: 2, label: 'Coach' },
  { n: 3, label: 'Times' },
  { n: 4, label: 'Confirm' },
] as const;

export function PrivateBookingWizard({
  academy,
  academySlug: _academySlug,
  config,
  coaches,
  students,
  onStudentsRefresh,
  onComplete,
  embedded = false,
}: Props) {
  const academyId = academy.id;
  const authUser = useAuthStore((s) => s.user);
  const { formatDate: formatDateForRegion } = useFormatDate({
    region: academy.region,
    publicContext: true,
  });

  const selfStudent = useMemo(() => students.find((s) => s.isSelf), [students]);
  const childrenStudents = useMemo(() => students.filter((s) => !s.isSelf), [students]);

  const [step, setStep] = useState(1);
  /** Mirrors public lesson booking: myself vs child / another player. */
  const [whoFor, setWhoFor] = useState<'myself' | 'other' | null>(null);
  const [studentId, setStudentId] = useState('');
  const [coachId, setCoachId] = useState('');

  const [coachAvailabilityRows, setCoachAvailabilityRows] = useState<CoachAvailability[]>([]);
  const [lessonOccupiedBlocks, setLessonOccupiedBlocks] = useState<
    { dayOfWeek: number; startTime: string; endTime: string }[]
  >([]);
  const [availabilityExceptions, setAvailabilityExceptions] = useState<CoachAvailabilityException[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  const [selectedSlots, setSelectedSlots] = useState<CalendarSessionSlot[]>([]);

  const [aggregatedPreview, setAggregatedPreview] = useState<{
    lineItems: Array<{
      key: string;
      sessionDate: string;
      dateLabel: string;
      startTime: string;
      endTime: string;
      totalAmount: number;
      generatedDates: string[];
      durationMinutes: number;
    }>;
    totalAmount: number;
    currency: string;
  } | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [coachDetailOpen, setCoachDetailOpen] = useState(false);
  const [coachDetailCoach, setCoachDetailCoach] = useState<Coach | null>(null);

  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [addStudentMode, setAddStudentMode] = useState<'child' | 'self'>('child');
  const [addStudentSubmitting, setAddStudentSubmitting] = useState(false);
  const [newStudent, setNewStudent] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'male' as 'male' | 'female' | 'other',
  });
  /** Until refreshed `students` includes the new id, sidebar shows this name. */
  const [summaryStudentFallback, setSummaryStudentFallback] = useState<{
    id: string;
    firstName: string;
    lastName: string;
  } | null>(null);

  const wizardTopRef = useRef<HTMLDivElement>(null);
  const pendingScrollToWizardTopRef = useRef(false);

  const todayYmd = useMemo(() => localTodayYmd(), []);
  const [weekStartMondayYmd, setWeekStartMondayYmd] = useState(() => mondayOfWeekContainingYmd(localTodayYmd()));

  const minWeekMonday = useMemo(() => mondayOfWeekContainingYmd(todayYmd), [todayYmd]);
  const maxWeekMonday = useMemo(() => addDaysYmd(minWeekMonday, 365 * 7), [minWeekMonday]);

  /** Fixed 60 min when academy allows; otherwise clamp to academy min. */
  const bookingDurationMinutes = useMemo(() => {
    const min = config.privateBookingMinDurationMinutes ?? 60;
    const max = config.privateBookingMaxDurationMinutes ?? 120;
    const preferred = 60;
    if (preferred >= min && preferred <= max) return preferred;
    return min;
  }, [config]);

  const minLeadHours = config.privateBookingMinLeadHours ?? 24;

  useEffect(() => {
    if (!summaryStudentFallback) return;
    if (students.some((s) => s.id === summaryStudentFallback.id)) {
      setSummaryStudentFallback(null);
    }
  }, [students, summaryStudentFallback]);

  /** If "For myself" was chosen while `students` was still loading, go to coach step once self profile is present. */
  useEffect(() => {
    if (step !== 1 || whoFor !== 'myself' || !selfStudent) return;
    if (studentId === selfStudent.id) return;
    setStudentId(selfStudent.id);
    setStep(2);
  }, [step, whoFor, selfStudent, studentId]);

  useEffect(() => {
    if (step !== 4 || !pendingScrollToWizardTopRef.current) return;
    pendingScrollToWizardTopRef.current = false;
    requestAnimationFrame(() => {
      wizardTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [step]);

  const summaryStudentLabel = useMemo(() => {
    const selected = students.find((s) => s.id === studentId);
    if (selected) {
      return `${selected.firstName} ${selected.lastName}`;
    }
    if (summaryStudentFallback && summaryStudentFallback.id === studentId) {
      return `${summaryStudentFallback.firstName} ${summaryStudentFallback.lastName}`;
    }
    return '—';
  }, [students, studentId, summaryStudentFallback]);

  useEffect(() => {
    if (!academyId || !coachId) {
      setCoachAvailabilityRows([]);
      setLessonOccupiedBlocks([]);
      setAvailabilityExceptions([]);
      return undefined;
    }
    let cancelled = false;
    setAvailabilityLoading(true);
    privateBookingService
      .getCoachAvailabilityAll(academyId, coachId, todayYmd)
      .then((data) => {
        if (cancelled) return;
        setCoachAvailabilityRows((data.weeklySlots as CoachAvailability[]) ?? []);
        setLessonOccupiedBlocks(data.lessonOccupiedBlocks ?? []);
        setAvailabilityExceptions((data.exceptions as CoachAvailabilityException[]) ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setCoachAvailabilityRows([]);
          setLessonOccupiedBlocks([]);
          setAvailabilityExceptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setAvailabilityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [academyId, coachId, todayYmd]);

  useEffect(() => {
    setWeekStartMondayYmd(mondayOfWeekContainingYmd(todayYmd));
  }, [coachId, todayYmd]);

  const selectedCoach = useMemo(() => coaches.find((c) => c.id === coachId), [coaches, coachId]);

  const mergedAvailabilityFromCoachRows = useMemo(() => {
    const raw = availabilityWindowsFromCoachRows(coachAvailabilityRows);
    return mergeAdjacentAvailabilityWindows(raw);
  }, [coachAvailabilityRows]);

  const rawMergedCoachAvailability = useMemo(() => {
    if (mergedAvailabilityFromCoachRows.length > 0) return mergedAvailabilityFromCoachRows;
    return getDefaultWeeklyWindowsWhenCoachAvailabilityUnset();
  }, [mergedAvailabilityFromCoachRows]);

  const availabilityWindows = useMemo(() => {
    const mergedBusy = mergeAdjacentAvailabilityWindows(lessonOccupiedBlocks);
    return subtractBusyFromWeeklyWindows(rawMergedCoachAvailability, mergedBusy);
  }, [rawMergedCoachAvailability, lessonOccupiedBlocks]);

  /**
   * Session-sized steps within the coach’s merged weekly “available” bands (before lesson blocks).
   * Each row is still validated against `availabilityWindows` + date exceptions in the calendar — busy
   * or blocked times stay visible as disabled buttons.
   */
  const calendarSlots = useMemo(() => {
    const step = config.privateBookingDurationStepMinutes ?? 30;
    const dur = bookingDurationMinutes;
    if (dur < (config.privateBookingMinDurationMinutes ?? 60) || dur > (config.privateBookingMaxDurationMinutes ?? 120)) {
      return [];
    }
    return buildSelectablePrivateSlots(rawMergedCoachAvailability, dur, step);
  }, [rawMergedCoachAvailability, config, bookingDurationMinutes]);

  /** Show notice only when the coach has calendar date exceptions (unavailable) that overlap the week on screen. */
  const showCoachDateExceptionsNotice = useMemo(() => {
    const blocking = availabilityExceptions.filter((ex) => !ex.isAvailable);
    if (blocking.length === 0) return false;
    for (let i = 0; i < 7; i++) {
      const ymd = addDaysYmd(weekStartMondayYmd, i);
      if (blocking.some((ex) => ymd >= ex.dateFrom && ymd <= ex.dateTo)) {
        return true;
      }
    }
    return false;
  }, [availabilityExceptions, weekStartMondayYmd]);

  useEffect(() => {
    setSelectedSlots((prev) =>
      prev.filter((s) => {
        if (s.sessionDate < todayYmd) return false;
        if (!isSessionStartAtLeastHoursAhead(s.sessionDate, s.startTime, minLeadHours)) return false;
        if (isPrivateSlotBlockedOnCalendarDate(s.sessionDate, s.startTime, s.endTime, availabilityExceptions)) {
          return false;
        }
        const template = { dayOfWeek: s.dayOfWeek, startTime: s.startTime, endTime: s.endTime };
        if (!calendarSlots.some((c) => privateSlotKey(c) === privateSlotKey(template))) return false;
        return isPrivateSlotWithinFreeWindows(template, availabilityWindows);
      })
    );
  }, [calendarSlots, availabilityWindows, availabilityExceptions, todayYmd, minLeadHours]);

  const selectedKeys = useMemo(
    () => selectedSlots.map((s) => calendarPrivateSlotKey(s.sessionDate, s.startTime, s.endTime)),
    [selectedSlots]
  );

  const toggleSlot = useCallback(
    (slot: CalendarSessionSlot) => {
      const template = { dayOfWeek: slot.dayOfWeek, startTime: slot.startTime, endTime: slot.endTime };
      if (!isPrivateSlotWithinFreeWindows(template, availabilityWindows)) return;
      if (isPrivateSlotBlockedOnCalendarDate(slot.sessionDate, slot.startTime, slot.endTime, availabilityExceptions)) {
        return;
      }
      if (!isSessionStartAtLeastHoursAhead(slot.sessionDate, slot.startTime, minLeadHours)) return;

      const k = calendarPrivateSlotKey(slot.sessionDate, slot.startTime, slot.endTime);
      setSelectedSlots((prev) => {
        const exists = prev.some(
          (s) => calendarPrivateSlotKey(s.sessionDate, s.startTime, s.endTime) === k
        );
        if (exists) return prev.filter((s) => calendarPrivateSlotKey(s.sessionDate, s.startTime, s.endTime) !== k);
        return [...prev, slot];
      });
      setAggregatedPreview(null);
      setPreviewError(null);
    },
    [availabilityWindows, availabilityExceptions, minLeadHours]
  );

  const submit = async () => {
    if (!academyId || !coachId || !studentId || selectedSlots.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const ordered = [...selectedSlots].sort(
        (a, b) => a.sessionDate.localeCompare(b.sessionDate) || a.startTime.localeCompare(b.startTime)
      );
      const sessionSlots = ordered.map((s) => ({
        sessionDate: s.sessionDate,
        startTime: s.startTime,
        endTime: s.endTime,
      }));
      const result = await privateBookingService.createRequest({
        academyId,
        studentId,
        coachId,
        sessionSlots,
      });
      if (result.bookingId) {
        onComplete({
          message:
            'Your private lesson package was created. Please complete payment from your bookings.',
          bookingId: result.bookingId,
        });
      } else {
        onComplete({
          message:
            'Your booking request was submitted. The academy will review it and you will receive an email when you can pay.',
        });
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const openAddStudentDialog = (mode: 'child' | 'self') => {
    setAddStudentMode(mode);
    if (mode === 'self' && authUser) {
      setNewStudent({
        firstName: authUser.firstName ?? '',
        lastName: authUser.lastName ?? '',
        dateOfBirth: '',
        gender: 'male',
      });
    } else {
      setNewStudent({ firstName: '', lastName: '', dateOfBirth: '', gender: 'male' });
    }
    setAddStudentOpen(true);
  };

  const handleAddStudent = async () => {
    if (!newStudent.firstName.trim() || !newStudent.lastName.trim() || !newStudent.dateOfBirth) {
      toast.error('Please fill in first name, last name, and date of birth.');
      return;
    }
    setAddStudentSubmitting(true);
    try {
      const res = await studentService.createStudent({
        firstName: newStudent.firstName.trim(),
        lastName: newStudent.lastName.trim(),
        dateOfBirth: new Date(newStudent.dateOfBirth),
        gender: newStudent.gender,
        academyId,
        ...(addStudentMode === 'self' ? { isSelf: true } : {}),
      });
      if (res.success && res.data?.id) {
        const newId = res.data.id;
        const fn = newStudent.firstName.trim();
        const ln = newStudent.lastName.trim();
        setStudentId(newId);
        setSummaryStudentFallback({ id: newId, firstName: fn, lastName: ln });
        if (addStudentMode === 'self') {
          setWhoFor('myself');
        }
        toast.success(addStudentMode === 'self' ? 'Your player profile was added' : 'Student added');
        setAddStudentOpen(false);
        setNewStudent({ firstName: '', lastName: '', dateOfBirth: '', gender: 'male' });
        await onStudentsRefresh();
        if (step === 1) {
          setStep(2);
        }
      } else {
        toast.error(res.error || 'Could not add student');
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not add student');
    } finally {
      setAddStudentSubmitting(false);
    }
  };

  const openCoachDetail = (c: Coach) => {
    setCoachDetailCoach(c);
    setCoachDetailOpen(true);
  };

  const formatDayLabel = useCallback(
    (ymd: string) => formatDateForRegion(new Date(ymd + 'T12:00:00'), academy.region ?? null),
    [formatDateForRegion, academy.region]
  );

  const runPreview = async () => {
    if (!academyId || !coachId || !studentId || selectedSlots.length === 0) return;
    setPreviewError(null);
    setError(null);
    setPreviewLoading(true);
    try {
      const ordered = [...selectedSlots].sort(
        (a, b) => a.sessionDate.localeCompare(b.sessionDate) || a.startTime.localeCompare(b.startTime)
      );
      const sessionSlots = ordered.map((s) => ({
        sessionDate: s.sessionDate,
        startTime: s.startTime,
        endTime: s.endTime,
      }));
      const p = await privateBookingService.preview({
        academyId,
        studentId,
        coachId,
        sessionSlots,
      });
      const currency = p.currency ?? 'AUD';
      const totalAmount = p.totalAmount;
      const lineItems = ordered.map((slot) => ({
        key: calendarPrivateSlotKey(slot.sessionDate, slot.startTime, slot.endTime),
        sessionDate: slot.sessionDate,
        dateLabel: formatDayLabel(slot.sessionDate),
        startTime: slot.startTime,
        endTime: slot.endTime,
        totalAmount:
          ordered.length > 0 ? p.totalAmount / ordered.length : 0,
        generatedDates: p.generatedDates,
        durationMinutes: p.durationMinutes,
      }));
      setAggregatedPreview({ lineItems, totalAmount, currency });
      pendingScrollToWizardTopRef.current = true;
      setStep(4);
    } catch (e: unknown) {
      setPreviewError(e instanceof Error ? e.message : 'Preview failed');
      setAggregatedPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const goBackFromConfirm = () => {
    setAggregatedPreview(null);
    setPreviewError(null);
    setStep(3);
  };

  const canGoToStep = (n: number) => {
    if (n === 1) return true;
    if (n === 2) return Boolean(studentId) || step >= 2;
    if (n === 3) return Boolean(studentId && coachId) || step >= 3;
    if (n === 4) return Boolean(aggregatedPreview) || step >= 4;
    return false;
  };

  const handleStepNav = (n: number) => {
    if (!canGoToStep(n)) return;
    if (n < 4) {
      setAggregatedPreview(null);
      setPreviewError(null);
    }
    setStep(n);
  };

  const stepCardClass = embedded ? 'border-0 bg-transparent shadow-none' : undefined;
  const summaryCardClass = embedded ? 'border border-border p-4 shadow-none' : undefined;
  const embeddedSummaryHeaderClass = embedded ? 'p-0 pb-2' : undefined;
  const embeddedSummaryContentClass = embedded ? 'p-0' : undefined;

  return (
    <div
      className={cn(
        embedded
          ? 'flex flex-col gap-4'
          : 'grid grid-cols-1 items-start gap-8 xl:grid-cols-[minmax(0,1fr)_340px]'
      )}
    >
      <div ref={wizardTopRef} className={cn('min-w-0 space-y-6', embedded && 'space-y-4')}>
        <nav
          aria-label="Booking steps"
          className={cn(
            'flex flex-wrap gap-2 border-b border-border pb-3',
            embedded && 'pb-2'
          )}
        >
          {STEPS.map((s) => (
            <button
              key={s.n}
              type="button"
              onClick={() => handleStepNav(s.n)}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                step === s.n
                  ? 'bg-primary text-primary-foreground'
                  : canGoToStep(s.n)
                    ? 'bg-muted text-foreground hover:bg-muted/80'
                    : 'bg-muted/30 text-muted-foreground'
              }`}
            >
              {s.n}. {s.label}
            </button>
          ))}
        </nav>

        {step === 1 && (
          <Card className={stepCardClass}>
            <CardHeader>
              <h2 className="text-lg font-semibold">Who is this for?</h2>
              <CardDescription>
                Same as group lesson booking: choose whether lessons are for you or for someone else you manage.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    setWhoFor('myself');
                    setAggregatedPreview(null);
                    if (selfStudent) {
                      setStudentId(selfStudent.id);
                      setStep(2);
                    } else {
                      setStudentId('');
                    }
                  }}
                  className={cn(
                    'flex min-h-[88px] flex-col items-start rounded-xl border p-4 text-left transition-colors',
                    whoFor === 'myself'
                      ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                      : 'border-border hover:bg-muted/40'
                  )}
                >
                  <User className="mb-2 h-5 w-5 text-primary" aria-hidden />
                  <span className="font-semibold text-foreground">For myself</span>
                  <span className="mt-1 text-sm text-muted-foreground">
                    Private lessons for you
                    {authUser?.firstName ? (
                      <span className="text-muted-foreground">
                        {' '}
                        ({authUser.firstName} {authUser.lastName})
                      </span>
                    ) : null}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setWhoFor('other');
                    setAggregatedPreview(null);
                    const current = students.find((s) => s.id === studentId);
                    if (current?.isSelf) {
                      setStudentId('');
                    }
                  }}
                  className={cn(
                    'flex min-h-[88px] flex-col items-start rounded-xl border p-4 text-left transition-colors',
                    whoFor === 'other'
                      ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                      : 'border-border hover:bg-muted/40'
                  )}
                >
                  <Users className="mb-2 h-5 w-5 text-primary" aria-hidden />
                  <span className="font-semibold text-foreground">For someone else</span>
                  <span className="mt-1 text-sm text-muted-foreground">A child or another player you book for</span>
                </button>
              </div>

              {whoFor === 'myself' && (
                <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
                  {selfStudent ? (
                    <button
                      type="button"
                      onClick={() => {
                        setStudentId(selfStudent.id);
                        setAggregatedPreview(null);
                        setStep(2);
                      }}
                      className={cn(
                        'w-full rounded-lg border p-3 text-left transition-colors',
                        studentId === selfStudent.id
                          ? 'border-primary bg-background ring-2 ring-primary/20'
                          : 'border-transparent hover:bg-muted/50'
                      )}
                    >
                      <div className="font-medium text-foreground">
                        {selfStudent.firstName} {selfStudent.lastName}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">Your profile at this academy</p>
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        We need a player profile for you at {academy.name} before you can continue. Use your account
                        name below or adjust it — then add date of birth and gender.
                      </p>
                      <Button type="button" onClick={() => openAddStudentDialog('self')}>
                        Create my player profile
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {whoFor === 'other' && (
                <div className="space-y-3">
                  {childrenStudents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No players on file yet for this academy. Add someone you book for.
                    </p>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {childrenStudents.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            setStudentId(s.id);
                            setAggregatedPreview(null);
                          }}
                          className={cn(
                            'rounded-xl border p-4 text-left transition-colors',
                            studentId === s.id
                              ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                              : 'border-border hover:bg-muted/40'
                          )}
                        >
                          <div className="font-medium text-foreground">
                            {s.firstName} {s.lastName}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  <Button type="button" variant="outline" onClick={() => openAddStudentDialog('child')}>
                    Add a student
                  </Button>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  disabled={!whoFor || !studentId}
                  onClick={() => setStep(2)}
                >
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className={stepCardClass}>
            <CardHeader>
              <h2 className="text-lg font-semibold">Choose a coach</h2>
              <CardDescription>Tap a coach for more about them, then select who you&apos;d like.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {coaches.map((c) => (
                  <div
                    key={c.id}
                    className={`rounded-xl border p-4 flex gap-4 ${
                      coachId === c.id ? 'border-primary ring-2 ring-primary/20' : 'border-border'
                    }`}
                  >
                    <div className="shrink-0">
                      {c.avatarUrl ? (
                        <img
                          src={c.avatarUrl}
                          alt=""
                          className="h-16 w-16 rounded-full object-cover border border-border"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center text-lg font-semibold text-muted-foreground">
                          {c.firstName[0]}
                          {c.lastName[0]}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-foreground">
                        {c.firstName} {c.lastName}
                      </div>
                      {c.hourlyRate != null && (
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {formatCurrencySafe(c.hourlyRate, config.currency)}/hr
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(c.specialties ?? []).slice(0, 4).map((t) => (
                          <Badge key={t} variant="secondary" className="text-xs">
                            {t}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Button type="button" variant="outline" size="sm" onClick={() => openCoachDetail(c)}>
                          Details
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            setCoachId(c.id);
                            setSelectedSlots([]);
                            setAggregatedPreview(null);
                          }}
                        >
                          {coachId === c.id ? 'Selected' : 'Select'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between pt-2">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button type="button" disabled={!coachId} onClick={() => setStep(3)}>
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className={stepCardClass}>
            <CardHeader>
              <h2 className="text-lg font-semibold">Pick times</h2>
              <CardDescription>
                Choose one or more {bookingDurationMinutes}-minute sessions on specific dates. Only today and future
                dates are shown. Each session must start at least {minLeadHours} hour{minLeadHours === 1 ? '' : 's'} from
                the time you book. Use the arrows to move between weeks.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedCoach && (
                <p className="text-sm text-muted-foreground">
                  Hourly rate:{' '}
                  <strong>
                    {config.currency === 'USD' ? '$' : '$'}
                    {selectedCoach.hourlyRate ?? '—'}/hr
                  </strong>{' '}
                  · {bookingDurationMinutes} min sessions
                </p>
              )}
              {availabilityLoading ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
                </div>
              ) : calendarSlots.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No time slots fit a {bookingDurationMinutes}-minute session within this coach&apos;s hours. Contact the
                  academy if you need help.
                </p>
              ) : (
                <>
                  {showCoachDateExceptionsNotice && (
                    <p className="text-sm text-muted-foreground text-center rounded-md border border-border bg-muted/40 px-3 py-2">
                      This coach has no availability during this period.
                    </p>
                  )}
                  <PrivateBookingWeekCalendar
                    todayYmd={todayYmd}
                    minLeadHours={minLeadHours}
                    weekStartMondayYmd={weekStartMondayYmd}
                    onWeekOffset={(delta) => {
                      setWeekStartMondayYmd((prev) => addDaysYmd(prev, delta * 7));
                    }}
                    minWeekStartMondayYmd={minWeekMonday}
                    maxWeekStartMondayYmd={maxWeekMonday}
                    slots={calendarSlots}
                    freeWindows={availabilityWindows}
                    exceptions={availabilityExceptions}
                    selectedKeys={selectedKeys}
                    onToggleSlot={toggleSlot}
                    formatDayDate={formatDayLabel}
                  />
                </>
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex justify-between pt-2">
                <Button type="button" variant="outline" onClick={() => setStep(2)}>
                  Back
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 4 && aggregatedPreview && (
          <Card className={stepCardClass}>
            <CardHeader>
              <h2 className={cn('font-semibold', embedded ? 'text-base' : 'text-lg')}>Price preview</h2>
              <CardDescription>Review your sessions and confirm your booking request.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <ul className="space-y-3">
                {aggregatedPreview.lineItems.map((line) => (
                  <li
                    key={line.key}
                    className="flex items-start gap-2 border-b border-border/60 pb-3 last:border-0 last:pb-0"
                  >
                    <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">
                        {line.dateLabel} · {formatTimeHHMM(line.startTime)} – {formatTimeHHMM(line.endTime)}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{line.durationMinutes} min</div>
                      <div className="mt-2 text-xs font-medium text-foreground">
                        {aggregatedPreview.currency} {line.totalAmount.toFixed(2)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="border-t pt-3 font-semibold">
                Total: {aggregatedPreview.currency} {aggregatedPreview.totalAmount.toFixed(2)}
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-between">
                <Button type="button" variant="outline" onClick={goBackFromConfirm}>
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Back to times
                </Button>
                <Button className="sm:min-w-48" disabled={submitting} onClick={() => void submit()}>
                  {submitting ? 'Submitting…' : 'Confirm booking request'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={coachDetailOpen} onOpenChange={setCoachDetailOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {coachDetailCoach ? (
                  <>
                    {coachDetailCoach.firstName} {coachDetailCoach.lastName}
                  </>
                ) : (
                  'Coach'
                )}
              </DialogTitle>
              <div className="space-y-3 text-left pt-2">
              {coachDetailCoach?.hourlyRate != null && (
                <p className="text-sm text-foreground">
                  <strong>
                    {config.currency === 'USD' ? '$' : '$'}
                    {coachDetailCoach.hourlyRate}/hr
                  </strong>
                </p>
              )}
              {coachDetailCoach?.bio ? (
                <p className="text-sm text-muted-foreground leading-relaxed">{coachDetailCoach.bio}</p>
              ) : (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  No bio has been added yet for this coach.
                </p>
              )}
              {coachDetailCoach?.specialties && coachDetailCoach.specialties.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {coachDetailCoach.specialties.map((t) => (
                    <Badge key={t} variant="secondary">
                      {t}
                    </Badge>
                  ))}
                </div>
              )}
              {coachDetailCoach && (
                <Button
                  type="button"
                  className="w-full mt-2"
                  onClick={() => {
                    setCoachId(coachDetailCoach.id);
                    setSelectedSlots([]);
                    setAggregatedPreview(null);
                    setCoachDetailOpen(false);
                    setStep(2);
                  }}
                >
                  Select this coach
                </Button>
              )}
            </div>
            </DialogHeader>
          </DialogContent>
        </Dialog>

        <Dialog open={addStudentOpen} onOpenChange={setAddStudentOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {addStudentMode === 'self' ? 'Your player profile' : 'Add a student'}
              </DialogTitle>
              <DialogDescription>
                {addStudentMode === 'self'
                  ? `This creates your profile at ${academy.name} so you can book private lessons for yourself.`
                  : `They will be linked to ${academy.name} for bookings.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm font-medium">First name</label>
                  <Input
                    value={newStudent.firstName}
                    onChange={(e) => setNewStudent((p) => ({ ...p, firstName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Last name</label>
                  <Input
                    value={newStudent.lastName}
                    onChange={(e) => setNewStudent((p) => ({ ...p, lastName: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Date of birth</label>
                <Input
                  type="date"
                  value={newStudent.dateOfBirth}
                  onChange={(e) => setNewStudent((p) => ({ ...p, dateOfBirth: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Gender</label>
                <select
                  className="input w-full mt-1"
                  value={newStudent.gender}
                  onChange={(e) =>
                    setNewStudent((p) => ({ ...p, gender: e.target.value as 'male' | 'female' | 'other' }))
                  }
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setAddStudentOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" disabled={addStudentSubmitting} onClick={() => void handleAddStudent()}>
                  {addStudentSubmitting ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <aside
        className={cn(
          embedded && step === 4
            ? 'hidden'
            : embedded
              ? 'space-y-3 border-t border-border pt-4'
              : 'space-y-4 xl:sticky xl:top-4 xl:self-start'
        )}
      >
        <Card className={summaryCardClass}>
          <CardHeader className={embeddedSummaryHeaderClass}>
            <h2 className={cn('font-semibold', embedded ? 'text-base' : 'text-lg')}>Summary</h2>
          </CardHeader>
          <CardContent className={cn('space-y-3 text-sm', embeddedSummaryContentClass)}>
            <p className="flex items-start gap-2">
              <User className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span>
                <span className="text-muted-foreground">Student:</span> {summaryStudentLabel}
              </span>
            </p>
            <p className="flex items-start gap-2">
              <GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span>
                <span className="text-muted-foreground">Coach:</span>{' '}
                {selectedCoach ? `${selectedCoach.firstName} ${selectedCoach.lastName}` : '—'}
              </span>
            </p>
            <p className="flex items-start gap-2">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span>
                <span className="text-muted-foreground">Sessions:</span> {bookingDurationMinutes} minutes each
              </span>
            </p>
            {selectedSlots.length > 0 && (
              <ul className="space-y-2 rounded-md border border-input bg-muted/30 p-3">
                {selectedSlots.map((s) => (
                  <li
                    key={calendarPrivateSlotKey(s.sessionDate, s.startTime, s.endTime)}
                    className="flex justify-between gap-2 text-xs sm:text-sm"
                  >
                    <span className="inline-flex min-w-0 items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      {formatDayLabel(s.sessionDate)} · {formatTimeHHMM(s.startTime)} –{' '}
                      {formatTimeHHMM(s.endTime)}
                    </span>
                    {step < 4 && (
                      <button
                        type="button"
                        className="shrink-0 text-primary underline"
                        onClick={() => toggleSlot(s)}
                      >
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {step < 4 && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  disabled={
                    previewLoading || !studentId || !coachId || selectedSlots.length === 0 || step < 3
                  }
                  onClick={() => void runPreview()}
                >
                  {previewLoading ? 'Loading…' : 'Proceed'}
                </Button>
                {previewError && <p className="text-xs text-destructive">{previewError}</p>}
              </>
            )}
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}
